import React, { createContext, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getFeatureFlags, updateFeatureFlag } from "../services/featureFlagsService";
import { useAuth } from './AuthContext';

export const FeatureFlagsContext = createContext();

export const FeatureFlagsProvider = ({ children }) => {
  const [featureFlags, setFeatureFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const loadFlags = useCallback(async () => {
    try {
      setLoading(true);
      const flags = await getFeatureFlags(navigate);
      setFeatureFlags(flags);
    } catch (error) {
      console.error("Error loading feature flags:", error);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      setFeatureFlags([]);
      setLoading(false);
      return;
    }

    loadFlags();
  }, [authLoading, loadFlags, user]);

  const isFeatureEnabled = (featureName) => {
    const flag = featureFlags.find((f) => f.id === featureName || f.nombre === featureName);
    if (!flag) return false;

    const state = flag.state || flag.estado;
    if (state === "disabled") return false;
    if (state === "everyone") return true;

    if (state === "admin_only") {
      return user?.rol === "admin";
    }

    return false;
  };

  const refreshFlags = async () => {
    await loadFlags();
  };

  const updateFlag = async (flagId, state) => {
    try {
      await updateFeatureFlag(flagId, { state }, navigate);
      await refreshFlags();
    } catch (error) {
      console.error("Error updating feature flag:", error);
      throw error;
    }
  };

  return (
    <FeatureFlagsContext.Provider
      value={{
        featureFlags,
        loading,
        isFeatureEnabled,
        refreshFlags,
        updateFlag,
      }}
    >
      {children}
    </FeatureFlagsContext.Provider>
  );
};
